<template>
  <!-- The site is branded as the person; Uncapped Works is the studio he
       founded, and sits under the name rather than over it.
       `over-night` is the whole trick: the nav carries no ground of its own, so
       wherever the page is dark the bar has to invert instead of dropping a
       pale lozenge on top of it. Which ground that is gets measured, not
       guessed — see sampleGround() below. -->
  <nav
    ref="navRef"
    class="nav"
    :class="{ scrolled, 'over-night': overNight, 'on-night': overNight }"
  >
    <div class="container nav-inner">
      <a
        href="#hero"
        class="nav-logo glass"
        :class="{ compact: scrolled }"
        aria-label="Björn Koemans"
        @click.prevent="scrollTo('hero')"
      >
        <span class="nav-logo-text">
          <span class="nav-logo-top">
            <span class="nav-logo-mark">BJÖRN KOEMANS</span
            ><span class="nav-logo-underscore"
              ><span class="nav-logo-underscore-inner">_</span></span
            >
          </span>
          <span class="nav-logo-tag">uncapped works</span>
        </span>
      </a>

      <div class="nav-links glass">
        <a
          v-for="s in sections"
          :key="s"
          class="nav-link"
          :class="{ active: active === s }"
          :href="`#${s}`"
          :aria-current="active === s ? 'true' : undefined"
          @click.prevent="scrollTo(s)"
        >
          {{ $t(`nav.${s}`) }}
        </a>
      </div>

      <div class="lang-pill glass" role="group" :aria-label="$t('mobile.language')">
        <button
          v-for="l in ['en', 'nl']"
          :key="l"
          type="button"
          class="lang-btn"
          :class="{ active: currentLocale === l }"
          :aria-pressed="currentLocale === l"
          @click="switchLanguage(l)"
        >
          {{ l.toUpperCase() }}
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const SECTIONS = ["work", "about", "contact"];
const ORDER = ["hero", ...SECTIONS];

/* Any element that paints a dark ground marks itself. StageSection already
   sets `on-night` on its section root (its night is full-bleed); a night card
   inset in a light section marks the card, not the section, so the light
   margin around it still reads as light. */
const NIGHT = ".on-night, [data-night]";

export default {
  name: "AppNavigation",
  setup() {
    const { locale } = useI18n();
    const navRef = ref(null);
    const scrolled = ref(false);
    const overNight = ref(true);
    const active = ref("hero");

    const currentLocale = computed(() => locale.value);

    // Cached so the scroll frame only ever reads scrollY plus the one hit
    // test. Re-measured on resize and on locale change, because NL/EN copy
    // changes section heights.
    let tops = [];
    let navH = 64;
    let onScroll = null;
    let onResize = null;
    let resizeTimer = null;

    const measure = () => {
      const el = navRef.value;
      if (el) navH = el.offsetHeight || navH;
      const y = window.scrollY;
      tops = ORDER.map((id) => {
        const node = document.getElementById(id);
        return node ? node.getBoundingClientRect().top + y : Infinity;
      });
    };

    /* The nav asks what is directly behind it rather than where the page has
       scrolled to — the old `scrollY < fold * 0.55` test only knew about the
       masthead and left the bar pale over the night stage and the night
       contact card. One hit test at the bar's own centre, in the scroll
       handler that already exists: no second listener, no per-section
       observer, no rect read inside a rAF. */
    const sampleGround = () => {
      const nav = navRef.value;
      if (!nav || !document.elementsFromPoint) return;

      const stack = document.elementsFromPoint(
        Math.round(window.innerWidth / 2),
        Math.round(navH / 2)
      );

      let night = false;
      for (let i = 0; i < stack.length; i++) {
        const el = stack[i];
        if (nav.contains(el)) continue; // the bar must not read its own class
        night = !!el.closest(NIGHT); // only the topmost element paints the ground
        break;
      }
      if (night !== overNight.value) overNight.value = night;
    };

    const switchLanguage = (lang) => {
      locale.value = lang;
      try {
        localStorage.setItem("locale", lang);
      } catch (e) {
        // a private window can refuse this; the choice just won't persist
      }
    };

    /* The global reduce clamp sets `scroll-behavior: auto !important`, but a
       scrollTo() that names `behavior: "smooth"` outranks the CSS property —
       so the preference has to be read here or a reduce user still gets the
       long animated jump. */
    const prefersReduce = () =>
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const scrollTo = (id) => {
      const el = document.getElementById(id);
      if (!el) return;
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - (navH + 12),
        behavior: prefersReduce() ? "auto" : "smooth",
      });
    };

    onMounted(() => {
      onScroll = () => {
        const y = window.scrollY;
        scrolled.value = y > 24;
        sampleGround();

        const mid = y + window.innerHeight * 0.35;
        for (let i = ORDER.length - 1; i >= 0; i--) {
          if (tops[i] <= mid) {
            active.value = ORDER[i];
            break;
          }
        }
      };

      onResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          measure();
          onScroll();
        }, 150);
      };

      measure();
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize, { passive: true });

      // Sections settle a frame late on a cold load (fonts, the masthead fold),
      // so take the measurement again once the first paint is behind us.
      nextTick(() => {
        measure();
        onScroll();
      });
    });

    watch(locale, () => {
      nextTick(() => {
        measure();
        if (onScroll) onScroll();
      });
    });

    onBeforeUnmount(() => {
      if (onScroll) window.removeEventListener("scroll", onScroll);
      if (onResize) window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
    });

    return {
      sections: SECTIONS,
      navRef,
      scrolled,
      overNight,
      active,
      currentLocale,
      switchLanguage,
      scrollTo,
    };
  },
};
</script>

<style scoped>
.nav {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 60;
  padding: var(--s-4) 0;
  transition: padding var(--d-3) var(--ease-out);
}
.nav.scrolled {
  padding: var(--s-2) 0;
}

.nav-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-5);
}

/* ── Wordmark ─────────────────────────────────────────────── */
.nav-logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px 22px;
  border-radius: var(--r-pill);
  transition: background-color var(--d-3) var(--ease-out),
    box-shadow var(--d-3) var(--ease-out);
}
/* The mark is the brand's chrome. It is the one gradient in the bar and it
   arrives inside the image — nothing here recolours it. */
.nav-logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}
.nav-logo-top {
  display: inline-flex;
  align-items: baseline;
}
.nav-logo-mark {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--ink);
  transition: color var(--d-3) var(--ease-out);
}
/* The underscore is the only copper in the bar until a language is picked:
   one small chromatic tick, not a colour scheme. */
.nav-logo-underscore-inner {
  color: var(--accent);
  animation: blink 1.4s steps(1) infinite;
}
@keyframes blink {
  0%,
  49% {
    opacity: 1;
  }
  50%,
  100% {
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .nav-logo-underscore-inner {
    animation: none;
    opacity: 1;
  }
}
.nav-logo-tag {
  font-size: 10.5px;
  letter-spacing: 0.06em;
  color: var(--quiet);
  max-height: 16px;
  opacity: 1;
  overflow: hidden;
  transition: max-height var(--d-5) var(--ease-out),
    opacity var(--d-3) var(--ease-out), color var(--d-3) var(--ease-out);
}
.nav-logo.compact .nav-logo-tag {
  max-height: 0;
  opacity: 0;
}

/* Over a night ground the warm ink reads at 1.2:1 — the wordmark and its
   underscore both go light rather than tinting toward the accent, which at
   copper on #14110e would be worse. */
.nav.over-night .nav-logo-mark,
.nav.over-night .nav-logo-underscore-inner {
  color: var(--on-night);
}
.nav.over-night .nav-logo-tag {
  color: var(--on-night-3);
}

/* ── Links & language ─────────────────────────────────────── */
.nav-logo,
.nav-links,
.lang-pill {
  border-radius: var(--r-pill);
  /* Fill only. These two carry `.glass`, so a transitioning box-shadow would
     repaint an 18px backdrop blur every frame for 320ms; the ring is a
     hairline and its swap is invisible at a hard section edge anyway. */
  transition: background-color var(--d-3) var(--ease-out);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: var(--s-1);
  padding: 5px;
}
.nav-link {
  padding: 9px 18px;
  border-radius: var(--r-pill);
  font-size: var(--t-sm);
  font-weight: 600;
  color: var(--grey);
  transition: color var(--d-1) ease, background-color var(--d-1) ease;
}
.nav-link:hover,
.nav-link.active {
  color: var(--ink);
  background: rgba(22, 19, 15, 0.06);
}

.lang-pill {
  display: flex;
  padding: 4px;
  gap: 2px;
}
.lang-btn {
  padding: 6px 12px;
  border-radius: var(--r-pill);
  font-family: var(--font-measure);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  color: var(--grey);
  transition: color var(--d-1) ease, background-color var(--d-1) ease;
}
/* Warm white rather than #fff: 4.8:1 on the copper, and it does not punch a
   cold hole in a warm bar. The rule is deliberately not overridden over the
   night ground — the accent is the accent on both. */
.lang-btn.active {
  background: var(--accent);
  color: #fdfaf6;
}

/* The glass is a white-based recipe, so over a night ground it reads as an
   opaque lozenge. Suppress the whole thing there and let the night through. */
.nav.over-night .nav-logo,
.nav.over-night .nav-links,
.nav.over-night .lang-pill {
  background-image: none;
  background-color: rgba(244, 241, 236, 0.07);
  box-shadow: inset 0 0 0 var(--hair-w) rgba(244, 241, 236, 0.15);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}
.nav.over-night .nav-link,
.nav.over-night .lang-btn {
  color: var(--on-night-2);
}
.nav.over-night .nav-link:hover,
.nav.over-night .nav-link.active {
  color: var(--on-night);
  background: rgba(244, 241, 236, 0.12);
}

/* ── Mobile: the links move into MobileBar ────────────────── */
@media (max-width: 720px) {
  /* Both live in MobileBar below this width — showing them twice was the
     one thing the mobile capture caught. */
  .nav-links,
  .lang-pill,
  .nav-logo-tag {
    display: none;
  }
}
</style>
